/**
 * VRG-719 — is there already an OPEN toast saying exactly this?
 *
 * Since VRG-607 an error toast persists until dismissed. The product form's Create button
 * fired one toast per failed attempt, so four clicks against a storage outage (prod,
 * 2026-10-17 00:26-00:27: four POSTs, four 400s) left four identical red "Something went
 * wrong" toasts stacked up the screen. Nothing was wrong with any one of them; the stack
 * read as four different failures.
 *
 * The rule: an error toast whose final title AND body match one that is still open (not
 * already closing, same corner) is not added again. The caller's intent, "tell the user
 * this failed", is already on screen.
 *
 * Deliberately narrow:
 *   - errors only. A repeated success ("Saved") is a fresh confirmation of a fresh action.
 *   - not when the caller supplied its OWN `action` or a `render`. Two "Product archived ·
 *     Undo" toasts are about two different products, and the second Undo must not vanish.
 *     The auto-attached "Report a problem" link is identical on every error toast, so it
 *     does not make two toasts different.
 * Compared AFTER the provider's sanitising, so two different engineering strings that both
 * became "Something went wrong" count as the same toast, which is what the reader sees.
 */
export interface DedupeCandidate {
  type?: string
  title?: string
  text?: string
  position?: string
  hasOwnAction?: boolean
  hasRender?: boolean
  closing?: boolean
}

const norm = (s?: string) => (s ?? '').trim().replace(/[.!?]+$/, '').toLowerCase()

export const findDuplicateToast = <T extends DedupeCandidate>(
  open: readonly T[],
  candidate: DedupeCandidate,
): T | undefined => {
  if (candidate.type !== 'error' || candidate.hasOwnAction || candidate.hasRender) return undefined
  return open.find(
    (t) =>
      !t.closing &&
      t.type === 'error' &&
      !t.hasOwnAction &&
      !t.hasRender &&
      t.position === candidate.position &&
      norm(t.title) === norm(candidate.title) &&
      norm(t.text) === norm(candidate.text),
  )
}
